import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { SelectionItem } from '../../../core/component/select/select.component';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { Dwelling } from '../../model/dwelling';
import { Parking } from '../../model/parking';
import { Premises } from '../../model/premises';
import { RentalObjectKey } from '../../model/rental-object-key';
import { KeyTypeService } from '../../service/data/key-type.service';
import { numberValue } from '../../../core/helper/number-value';

@Component({
    selector: 'app-esign-rental-object-keys',
    templateUrl: './esign-rental-object-keys.component.html',
    styleUrls: ['./esign-rental-object-keys.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EsignRentalObjectKeysComponent implements OnInit, OnDestroy {
  constructor(
    private _keyTypeService: KeyTypeService,
    private _fb: FormBuilder,
    private _cf: FormControlExFactory
  ) {}

  private _forms: FormArray;
  @Input() set forms(value: AbstractControl) {
    this._forms = value as FormArray;
  }
  get forms(): FormArray {
    return this._forms;
  }
  @Input() keys: RentalObjectKey[];
  @Input() rentalObject: Dwelling | Premises | Parking;
  private _subscription = new Subscription();

  keyTypes: SelectionItem[] = [];

  addKey() {
    this.forms.controls.push(
      this.createForm(
        new RentalObjectKey({
          quantity: 1,
          quantityLentOut: 0,
          keyTypeId: this.keyTypes.length > 0 ? this.keyTypes[0].value : null,
        })
      )
    );
    this.forms.markAsDirty();
  }

  delete(keyForm: FormGroup) {
    this.forms.controls.splice(this.forms.controls.indexOf(keyForm), 1);
  }

  ngOnDestroy(): void {
    if (this._subscription) this._subscription.unsubscribe();
  }

  initForm() {
    let keys = this.rentalObject ? this.rentalObject.keys : this.keys;
    for (let key of keys) {
      let group = this.createForm(key);
      this.forms.controls.push(group);
    }
  }

  private createForm(key: RentalObjectKey): FormGroup {
    let group = this._fb.group({
      id: this._cf.createHidden(key.id),
      keyTypeId: this._cf.createMandatory('KEYTYPE.KEYTYPE', key.keyTypeId),
      quantity: this._cf.createWithOptions('RENTALOBJECT_KEY.QUANTITY', key.quantity, { isReadonly: true, minValue: 0 }),
      quantityLentOut: this._cf.createWithOptions('RENTALOBJECT_KEY.ESIGN_PREIVOUSLY_LENT_OUT', key.quantityLentOut, {
        required: true,
        validator: Validators.min(0),
      }),
      keysToHandOut: this._cf.createWithOptions('RENTALOBJECT_KEY.ESIGN_KEYCOUNT', key.quantity - key.quantityLentOut, {
        required: true,
        validator: Validators.min(0),
      }),
      quantityInStorage: this._cf.createWithOptions('RENTALOBJECT_KEY.ESIGN_REMAINING_IN_STORAGE', 0, {
        required: true,
        validator: Validators.min(0),
      }),
      systemNumber: this._cf.createMaxLength('RENTALOBJECT_KEY.SYSTEM_NUMBER', key.systemNumber, 100),
      marking: this._cf.createMaxLength('RENTALOBJECT_KEY.MARKING', key.marking, 100),
      caption: this._cf.createMaxLength('RENTALOBJECT_KEY.CAPTION', key.caption, 100),
      supplier: this._cf.createMaxLength('RENTALOBJECT_KEY.SUPPLIER', key.supplier, 100),
    });

    this._subscription.add(group.controls['keyTypeId'].valueChanges.subscribe(val => this.forms.markAsDirty()));
    this._subscription.add(group.controls['quantity'].valueChanges.subscribe(val => this.forms.markAsDirty()));
    this._subscription.add(
      group.controls['quantityLentOut'].valueChanges.subscribe(val => {
        this.forms.markAsDirty();
        this.updateQuantity(group);
      })
    );
    this._subscription.add(
      group.controls['quantityInStorage'].valueChanges.subscribe(val => {
        this.forms.markAsDirty();
        this.updateQuantity(group);
      })
    );
    this._subscription.add(
      group.controls['keysToHandOut'].valueChanges.subscribe(val => {
        this.forms.markAsDirty();
        this.updateStorage(group);
      })
    );
    this._subscription.add(group.controls['systemNumber'].valueChanges.subscribe(val => this.forms.markAsDirty()));
    this._subscription.add(group.controls['marking'].valueChanges.subscribe(val => this.forms.markAsDirty()));
    this._subscription.add(group.controls['caption'].valueChanges.subscribe(val => this.forms.markAsDirty()));
    this._subscription.add(group.controls['supplier'].valueChanges.subscribe(val => this.forms.markAsDirty()));
    return group;
  }

  ngOnInit(): void {
    this.initForm();
    this._subscription.add(
      this._keyTypeService.getList().subscribe(list => {
        this.keyTypes = list.map(t => new SelectionItem(t.name, t.id));
      })
    );
  }

  updateQuantity(group: FormGroup) {
    const currentStorage = numberValue(group.controls['quantityInStorage'].value);
    const keysToHandOut = numberValue(group.controls['keysToHandOut'].value);
    const lentOut = numberValue(group.controls['quantityLentOut'].value);
    group.controls['quantity'].setValue(currentStorage + keysToHandOut + lentOut);
  }

  updateStorage(group: FormGroup) {
    const currentStorage = numberValue(group.controls['quantityInStorage'].value);
    const keysToHandOut = numberValue(group.controls['keysToHandOut'].value);

    group.controls['quantityInStorage'].setValue(currentStorage < keysToHandOut ? 0 : currentStorage - keysToHandOut);
  }
}
